<template>
  <div class="perks">
    <!-- 页头：运营者专属批发优惠 -->
    <div class="perks__head">
      <div class="perks__head-text">
        <h1 class="perks__title"><i class="el-icon-present"></i> {{ $t("perks.title") }}</h1>
        <p class="perks__subtitle">{{ $t("perks.subtitle") }}</p>
      </div>
    </div>

    <!-- 加载 / 失败 / 空态 -->
    <div v-if="loading" class="perks__state" v-loading="true" :element-loading-text="$t('common.loading')"></div>

    <div v-else-if="error" class="perks__state">
      <i class="el-icon-warning-outline"></i>
      <div class="perks__state-title">{{ $t("common.loadFailed") }}</div>
      <div class="perks__state-hint">{{ $t("common.loadFailedHint") }}</div>
      <el-button size="small" icon="el-icon-refresh" @click="load">{{ $t("common.retry") }}</el-button>
    </div>

    <div v-else-if="!offers.length" class="perks__state">
      <i class="el-icon-present"></i>
      <div class="perks__state-title">{{ $t("perks.empty") }}</div>
      <div class="perks__state-hint">{{ $t("perks.emptyHint") }}</div>
    </div>

    <!-- 优惠卡片：整卡可点，进入详情 -->
    <div v-else class="perks__grid">
      <div
        v-for="offer in offers"
        :key="offer.id"
        class="perk-card"
        role="button"
        tabindex="0"
        @click="openDetail(offer)"
        @keyup.enter="openDetail(offer)"
      >
        <div class="perk-card__media">
          <el-image :src="offer.heroImage" fit="cover" class="perk-card__img">
            <div slot="error" class="perk-card__ph"><i class="el-icon-present"></i></div>
            <div slot="placeholder" class="perk-card__ph"><i class="el-icon-loading"></i></div>
          </el-image>
          <span v-if="offer.categoryName" class="perk-card__tag">{{ offer.categoryName }}</span>
          <span v-if="offer.badge" class="perk-card__badge">{{ offer.badge }}</span>
        </div>
        <div class="perk-card__body">
          <div class="perk-card__brand vtm-mono">{{ offer.partner || offer.title }}</div>
          <div class="perk-card__title">{{ offer.title }}</div>
          <div v-if="priceText(offer)" class="perk-card__price">
            <span class="perk-card__price-val">{{ priceText(offer) }}</span>
            <span v-if="offer.unit && !offer.priceLabel" class="perk-card__price-unit">
              {{ $t("perks.perUnit", { unit: offer.unit }) }}
            </span>
          </div>
          <div class="perk-card__blurb">{{ offer.description }}</div>
        </div>
        <div class="perk-card__actions">
          <el-button type="primary" size="small" icon="el-icon-view" @click.stop="openDetail(offer)">
            {{ $t("perks.viewOffer") }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 底部说明 -->
    <div class="perks__foot"><i class="el-icon-info"></i> {{ $t("perks.footNote") }}</div>

    <!-- 优惠详情：点击卡片进入 -->
    <el-drawer
      :visible.sync="detailVisible"
      :with-header="false"
      size="560px"
      append-to-body
      custom-class="perk-drawer"
    >
      <div v-if="active" class="pd">
        <div class="pd__head" :style="{ background: accentOf(active) }">
          <button type="button" class="pd__close" @click="detailVisible = false">
            <i class="el-icon-close"></i>
          </button>
          <div class="pd__partner">{{ active.partner || active.title }}</div>
          <div class="pd__title">{{ active.title }}</div>
          <span v-if="active.badge" class="pd__badge">{{ active.badge }}</span>
        </div>

        <div class="pd__body">
          <el-image v-if="active.heroImage" :src="active.heroImage" fit="contain" class="pd__hero">
            <div slot="error" class="perk-card__ph"><i class="el-icon-present"></i></div>
          </el-image>

          <!-- 限时 / 首单优惠 -->
          <div v-if="active.offer" class="pd__offer">
            <i class="el-icon-star-on"></i> {{ active.offer }}
          </div>

          <!-- 价格 -->
          <div v-if="priceText(active)" class="pd__section">
            <div class="pd__label">{{ $t("perks.pricing") }}</div>
            <div class="pd__price">
              <span class="pd__price-val">{{ priceText(active) }}</span>
              <span v-if="active.unit && !active.priceLabel" class="pd__price-unit">
                {{ $t("perks.perUnit", { unit: active.unit }) }}
              </span>
              <span v-if="active.taxable && !active.priceLabel" class="pd__price-tax">
                {{ $t("perks.plusTax") }}
              </span>
            </div>
            <div v-if="active.priceNote" class="pd__note">{{ active.priceNote }}</div>
          </div>

          <p v-if="active.description" class="pd__desc">{{ active.description }}</p>

          <!-- 图集（口味 / 款式） -->
          <div v-if="active.gallery && active.gallery.length" class="pd__section">
            <div class="pd__label">
              {{ active.galleryTitle || $t("perks.flavors") }}
              <span v-if="active.galleryNote" class="pd__note pd__note--inline">{{ active.galleryNote }}</span>
            </div>
            <div class="pd__gallery">
              <div v-for="(g, i) in active.gallery" :key="i" class="pd__gitem">
                <el-image :src="g.image" fit="contain" class="pd__gimg">
                  <div slot="error" class="perk-card__ph"><i class="el-icon-picture-outline"></i></div>
                </el-image>
                <div v-if="g.label" class="pd__glabel">{{ g.label }}</div>
              </div>
            </div>
          </div>

          <!-- 无图时用文字标签展示口味 -->
          <div v-else-if="active.flavors && active.flavors.length" class="pd__section">
            <div class="pd__label">
              {{ active.galleryTitle || $t("perks.flavors") }}
              <span v-if="active.galleryNote" class="pd__note pd__note--inline">{{ active.galleryNote }}</span>
            </div>
            <div class="pd__chips">
              <el-tag v-for="(f, i) in active.flavors" :key="i" size="small" type="info">{{ f }}</el-tag>
            </div>
          </div>

          <!-- 参与条件 -->
          <div v-if="active.requirements && active.requirements.length" class="pd__section">
            <div class="pd__label">{{ $t("perks.requirements") }}</div>
            <ul class="pd__reqs">
              <li v-for="(r, i) in active.requirements" :key="i" :class="{ 'is-met': r.met }">
                <i :class="r.met ? 'el-icon-circle-check' : 'el-icon-circle-plus-outline'"></i>
                {{ r.label }}
              </li>
            </ul>
          </div>

          <!-- 经销商注册说明 -->
          <div v-if="active.resellerNote" class="pd__reseller">
            <i class="el-icon-info"></i> {{ active.resellerNote }}
          </div>

          <!-- action='direct'：直接展示订购联系方式 -->
          <div v-if="active.action === 'direct' && active.contactInfo" class="pd__section">
            <div class="pd__label">{{ $t("perks.howToOrder") }}</div>
            <div class="pd__contact">{{ active.contactInfo }}</div>
          </div>
        </div>

        <!-- action='link'：跳转批发站点 -->
        <div v-if="active.action !== 'direct' && active.actionUrl" class="pd__foot">
          <el-button type="primary" icon="el-icon-shopping-cart-2" @click="shop(active)">
            {{ $t("perks.buyWholesale") }}
          </el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
  import config from "@/http/config";

  export default {
    name: "Perks",
    data() {
      return {
        offers: [],
        loading: true,
        error: false,
        detailVisible: false,
        active: null
      };
    },
    created() {
      this.load();
    },
    methods: {
      // 服务端相对路径补全为绝对地址；已是绝对地址则原样返回
      resolveImg(raw) {
        const s = (raw || "").trim();
        if (!s) return "";
        if (/^https?:\/\//i.test(s) || s.startsWith("data:")) return s;
        return (config.baseUrl || "").replace(/\/+$/, "") + "/" + s.replace(/^\/+/, "");
      },
      // gallery / flavors / requirements 服务端以 JSON 文本存储
      parseJson(value) {
        if (value == null || value === "") return undefined;
        if (typeof value !== "string") return value;
        try {
          return JSON.parse(value);
        } catch (e) {
          return undefined;
        }
      },
      toEpoch(v) {
        if (v == null || v === "") return null;
        if (typeof v === "number") return v;
        const t = Date.parse(String(v).replace(" ", "T"));
        return Number.isFinite(t) ? t : null;
      },
      // 与移动端 normalizePromotion 保持一致
      normalize(raw) {
        const gallery = this.parseJson(raw.gallery) || [];
        const hero = raw.logoImg || (gallery[0] && gallery[0].image) || "";
        return {
          ...raw,
          id: String(raw.id == null ? "" : raw.id),
          price: raw.price != null && raw.price !== "" ? Number(raw.price) : null,
          gallery: gallery.map(g => ({ ...g, image: this.resolveImg(g.image) })),
          flavors: this.parseJson(raw.flavors) || [],
          requirements: this.parseJson(raw.requirements) || [],
          beginDate: this.toEpoch(raw.beginDate),
          endDate: this.toEpoch(raw.endDate),
          heroImage: this.resolveImg(hero)
        };
      },
      // 上下线时间窗内才展示（未设置视为不限）
      isLive(p, now) {
        return (p.beginDate == null || p.beginDate <= now) && (p.endDate == null || p.endDate >= now);
      },
      load() {
        this.loading = true;
        this.error = false;
        this.$api
          .request(this.$url.Promotion.page, this.$method.get, { status: 1, pageIndex: 1, pageSize: 100 })
          .then(res => {
            if (res && res.code === 0 && res.data) {
              const now = Date.now();
              this.offers = (res.data.records || [])
                .map(r => this.normalize(r))
                .filter(p => this.isLive(p, now))
                .sort((a, b) => (a.sort || 0) - (b.sort || 0));
            } else {
              this.error = true;
            }
          })
          .catch(() => {
            this.error = true;
          })
          .finally(() => {
            this.loading = false;
          });
      },
      priceText(offer) {
        if (!offer) return "";
        if (offer.priceLabel) return offer.priceLabel;
        if (offer.price == null || isNaN(offer.price)) return "";
        const sym = offer.currencySymbol || "$";
        return sym + Number(offer.price).toFixed(2);
      },
      accentOf(offer) {
        const a = offer && offer.accentColor;
        return /^#[0-9a-f]{3,8}$/i.test(a || "") ? a : "var(--vtm-navy)";
      },
      openDetail(offer) {
        this.active = offer;
        this.detailVisible = true;
        // 详情可能带更完整的图集/条件，拿到后再覆盖
        this.$api
          .request(this.$url.Promotion.detail, this.$method.get, { id: offer.id })
          .then(res => {
            if (res && res.code === 0 && res.data && this.active && this.active.id === offer.id) {
              this.active = this.normalize(res.data);
            }
          })
          .catch(() => {
            /* 列表数据已足够渲染，详情失败时静默降级 */
          });
      },
      shop(offer) {
        if (offer && offer.actionUrl) {
          window.open(offer.actionUrl, "_blank", "noopener");
        }
      }
    }
  };
</script>

<style scoped lang="scss">
  .perks {
    /* 页面底色用更深的 well 色，让白色卡片明显浮起 */
    padding: 20px 24px 32px;
    min-height: 100%;
    background: var(--vtm-well);
    border-radius: var(--vtm-r-md);
  }

  .perks__head {
    margin-bottom: 18px;
  }
  .perks__title {
    margin: 0;
    font-family: var(--vtm-display);
    font-size: 22px;
    font-weight: 600;
    color: var(--vtm-heading);

    i {
      color: var(--vtm-mint-ink);
      margin-right: 6px;
    }
  }
  .perks__subtitle {
    margin: 5px 0 0;
    font-size: 13.5px;
    color: var(--vtm-ink-2);
  }

  .perks__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
  }

  .perk-card {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    background: var(--vtm-surface);
    overflow: hidden;
    /* 与页面底色拉开层次：默认带投影，悬停加强 */
    box-shadow: var(--vtm-e1);
    transition: box-shadow var(--vtm-speed) ease, transform var(--vtm-speed) ease;

    &:hover {
      box-shadow: var(--vtm-e3, var(--vtm-e2));
      transform: translateY(-2px);
    }
  }

  .perk-card__media {
    position: relative;
    height: 170px;
    background: var(--vtm-well);
  }
  .perk-card__img {
    width: 100%;
    height: 100%;
    display: block;
  }
  .perk-card__ph {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 34px;
    color: var(--vtm-ink-3);
  }
  .perk-card__tag {
    position: absolute;
    top: 10px;
    left: 10px;
    padding: 3px 9px;
    border-radius: 999px;
    background: var(--vtm-navy);
    color: var(--vtm-mint);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .perk-card__body {
    flex: 1;
    padding: 14px 16px 6px;
  }
  .perk-card__brand {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: var(--vtm-mint-ink);
  }
  .perk-card__title {
    margin-top: 4px;
    font-family: var(--vtm-display);
    font-size: 16px;
    font-weight: 600;
    color: var(--vtm-heading);
    line-height: 1.35;
  }
  .perk-card__blurb {
    margin-top: 6px;
    font-size: 12.5px;
    line-height: 1.55;
    color: var(--vtm-ink-2);
  }

  .perk-card__actions {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 16px 14px;
  }

  .perks__foot {
    margin-top: 18px;
    font-size: 12.5px;
    color: var(--vtm-ink-3);

    i {
      margin-right: 5px;
    }
  }
  /* ---- 卡片可点 + 新增元素 ---- */
  .perk-card {
    cursor: pointer;
  }

  .perk-card:focus-visible {
    outline: 2px solid var(--vtm-mint);
    outline-offset: 2px;
  }

  .perk-card__badge {
    position: absolute;
    top: 10px;
    right: 10px;
    padding: 3px 8px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-size: 11px;
    font-weight: 500;
  }

  .perk-card__price {
    display: flex;
    align-items: baseline;
    gap: 5px;
    margin: 2px 0 6px;
  }

  .perk-card__price-val {
    font-size: 16px;
    font-weight: 600;
    color: var(--vtm-heading);
  }

  .perk-card__price-unit {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }

  /* ---- 加载 / 失败 / 空态 ---- */
  .perks__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 240px;
    text-align: center;
    color: var(--vtm-ink-3);

    > i {
      font-size: 30px;
      margin-bottom: 2px;
    }
  }

  .perks__state-title {
    font-size: 15px;
    font-weight: 500;
    color: var(--vtm-ink);
  }

  .perks__state-hint {
    font-size: 13px;
    margin-bottom: 8px;
  }

  /* ---- 优惠详情抽屉 ---- */
  .pd {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .pd__head {
    position: relative;
    flex-shrink: 0;
    padding: 22px 52px 20px 24px;
    color: #fff;
  }

  .pd__close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 30px;
    height: 30px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
    font-size: 15px;
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.32);
    }
  }

  .pd__partner {
    font-size: 12px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    opacity: 0.85;
  }

  .pd__title {
    margin-top: 3px;
    font-family: var(--vtm-display);
    font-size: 20px;
    font-weight: 600;
    line-height: 1.25;
  }

  .pd__badge {
    display: inline-block;
    margin-top: 9px;
    padding: 3px 9px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.22);
    font-size: 11px;
    font-weight: 500;
  }

  .pd__body {
    flex: 1;
    overflow-y: auto;
    padding: 18px 24px 24px;
  }

  .pd__hero {
    width: 100%;
    height: 190px;
    border-radius: var(--vtm-r-md);
    background: var(--vtm-well);
    margin-bottom: 16px;
  }

  .pd__offer {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    padding: 11px 13px;
    border-radius: var(--vtm-r-sm);
    background: var(--vtm-mint-soft);
    color: var(--vtm-mint-ink);
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 16px;

    i {
      margin-top: 1px;
    }
  }

  .pd__section {
    margin-bottom: 18px;
  }

  .pd__label {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--vtm-ink-3);
    margin-bottom: 7px;
  }

  .pd__price {
    display: flex;
    align-items: baseline;
    gap: 7px;
  }

  .pd__price-val {
    font-size: 22px;
    font-weight: 600;
    color: var(--vtm-heading);
  }

  .pd__price-unit,
  .pd__price-tax {
    font-size: 12.5px;
    color: var(--vtm-ink-3);
  }

  .pd__note {
    font-size: 12px;
    color: var(--vtm-ink-3);
    margin-top: 3px;

    &--inline {
      margin-top: 0;
      text-transform: none;
      letter-spacing: 0;
      font-weight: 400;
    }
  }

  .pd__desc {
    margin: 0 0 18px;
    font-size: 13.5px;
    line-height: 1.6;
    color: var(--vtm-ink-2);
  }

  .pd__gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 10px;
  }

  .pd__gitem {
    text-align: center;
  }

  .pd__gimg {
    width: 100%;
    height: 92px;
    border-radius: var(--vtm-r-sm);
    background: var(--vtm-well);
  }

  .pd__glabel {
    margin-top: 4px;
    font-size: 11.5px;
    line-height: 1.3;
    color: var(--vtm-ink-2);
  }

  .pd__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .pd__reqs {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: flex-start;
      gap: 7px;
      font-size: 13px;
      color: var(--vtm-ink-2);
      padding: 3px 0;

      i {
        margin-top: 2px;
        color: var(--vtm-ink-3);
      }

      &.is-met i {
        color: var(--vtm-ok);
      }
    }
  }

  .pd__reseller {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    padding: 11px 13px;
    border-radius: var(--vtm-r-sm);
    background: var(--vtm-info-soft);
    color: var(--vtm-info);
    font-size: 12.5px;
    line-height: 1.5;
    margin-bottom: 18px;

    i {
      margin-top: 1px;
    }
  }

  .pd__contact {
    padding: 11px 13px;
    border-radius: var(--vtm-r-sm);
    background: var(--vtm-well);
    font-size: 13px;
    line-height: 1.6;
    color: var(--vtm-ink);
    white-space: pre-wrap;
  }

  .pd__foot {
    flex-shrink: 0;
    padding: 14px 24px;
    border-top: 1px solid var(--vtm-line);
    background: var(--vtm-surface);

    ::v-deep .el-button {
      width: 100%;
    }
  }
</style>

<style lang="scss">
  /* el-drawer 挂载在 body 下，需非 scoped 覆盖内边距 */
  .perk-drawer .el-drawer__body {
    padding: 0;
    overflow: hidden;
  }
</style>
